import { useState, type FormEvent, type ReactNode } from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { AutoComplete } from "@/core/components/ui/autocomplete";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { cn } from "@/core/utils/utils";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import {
  STATEMENT_ITEM_ACTION_INTENT,
  StatementRenderer,
  referenceKey,
} from "@/payroll/ai-script/statement/statement-renderer";
import {
  useGetManagedPackStartOptions,
  useStartManagedPackThread,
} from "@/payroll/pack-chat/manager-query";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { getStatementItemReferences } from "@/payroll/pack-chat/statement-items";
import type { IPackChatThreadDetail } from "@/payroll/pack-chat/types";
import { SelectedStatementItemPreview } from "./selected-statement-item-preview";

const MAX_MESSAGE_LENGTH = 5000;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack fullWidth gap="xs">
    <Typography variant="subtitleW3">{label}</Typography>
    {children}
  </Stack>
);

/** Why the pack pane is empty, in the pack pane's own centred column. */
const PackPaneNote = ({
  children,
  destructive,
}: {
  children: string;
  destructive?: boolean;
}) => (
  <Stack fullWidth align="center" className="px-5 py-10">
    <Typography
      variant="bodyW3"
      textColor={destructive ? undefined : "tertiary"}
      className={cn("text-center", { "text-destructive": destructive })}
    >
      {children}
    </Typography>
  </Stack>
);

export const ManagerStartThreadForm = ({
  businessId,
  periodStart,
  activeRunId,
  onCancel,
  onStarted,
}: {
  businessId: string | null;
  periodStart: string;
  /** The run whose workbook Pay Breakdown is showing for this period. */
  activeRunId: string | null;
  onCancel: () => void;
  /** The created thread, so the caller can select its technician and open it. */
  onStarted: (thread: IPackChatThreadDetail) => void;
}) => {
  const [technicianId, setTechnicianId] = useState<string>();
  const [technicianSearch, setTechnicianSearch] = useState("");
  const [itemKey, setItemKey] = useState<string>();
  const [body, setBody] = useState("");
  const optionsQuery = useGetManagedPackStartOptions(
    businessId,
    periodStart,
    Boolean(activeRunId),
  );
  const startThread = useStartManagedPackThread(businessId, periodStart);

  // The page owns which completed workbook is active. Start options still carry
  // historical runs because other PackChat reads need them, so this form keeps
  // only technicians who have a pack in the page's active run.
  const options = optionsQuery.data?.start_options ?? [];
  const technicians = activeRunId
    ? options
        .filter((option) =>
          option.ai_script_runs.some((run) => run.id === activeRunId),
        )
        .map((option) => option.technician)
    : [];
  const selectedTechnician = technicians.find(
    (technician) => technician.id === technicianId,
  );

  const statementQuery = useGetAiScriptStatement(businessId, activeRunId, {
    identity: STATEMENT_IDENTITY.technician,
    technicianId: selectedTechnician?.id ?? null,
    enabled: Boolean(activeRunId && selectedTechnician),
  });
  const items = getStatementItemReferences(statementQuery.data?.statement);
  const selectedItem = items.find((item) => referenceKey(item) === itemKey);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTechnician || !activeRunId || !selectedItem || !body.trim())
      return;

    try {
      const response = await startThread.mutateAsync({
        technicianId: selectedTechnician.id,
        runId: activeRunId,
        context: selectedItem,
        body: body.trim(),
      });
      toast.success("Thread started", {
        description: `${selectedTechnician?.name ?? "The technician"} has been notified.`,
      });
      onStarted(response.thread);
    } catch (error) {
      toast.error("Thread not started", {
        description: getErrorMessage(error),
      });
    }
  };

  const unavailable = !activeRunId ? (
    <Typography variant="bodyW3" textColor="secondary">
      There is no active payroll run for this period. Complete a payroll run
      before you start a thread.
    </Typography>
  ) : optionsQuery.isError ? (
    <PackChatState
      error={optionsQuery.error}
      title="PayPacks couldn't be loaded"
      body="Try again. If it keeps failing, contact support."
      compact
      action={
        <Button
          variant="secondary"
          size="sm"
          loading={optionsQuery.isFetching}
          className="mt-2"
          onClick={() => void optionsQuery.refetch()}
        >
          Try again
        </Button>
      }
    />
  ) : !optionsQuery.isLoading && technicians.length === 0 ? (
    <Typography variant="bodyW3" textColor="secondary">
      No PayPack in the active run is visible to you, so there is nobody to
      start a thread with.
    </Typography>
  ) : null;

  // Every figure on the pack carries a handle here, which is the opposite of the
  // reading view's rule. There the handles competed with the marks that say where
  // the unanswered questions are; picking one is the whole job of this screen, and
  // there are no marks to drown.
  const pickerPane = !activeRunId ? (
    <PackPaneNote>There is no active PayPack for this period.</PackPaneNote>
  ) : !selectedTechnician ? (
    <PackPaneNote>Choose a technician to see their PayPack.</PackPaneNote>
  ) : statementQuery.isLoading ? (
    <Stack fullWidth align="center" className="py-10">
      <LoadingSpinner
        role="status"
        aria-label="Loading the PayPack"
        size="sm"
        color="secondary"
      />
    </Stack>
  ) : statementQuery.isError ? (
    <PackPaneNote destructive>That PayPack couldn't be loaded.</PackPaneNote>
  ) : !statementQuery.data?.statement ? (
    <PackPaneNote>No PayPack for this technician in this run.</PackPaneNote>
  ) : items.length === 0 ? (
    <PackPaneNote>This PayPack has no figures to point at.</PackPaneNote>
  ) : (
    <StatementRenderer
      statement={statementQuery.data.statement}
      showTitle={false}
      itemAction={{
        activeKey: itemKey,
        intent: STATEMENT_ITEM_ACTION_INTENT.startThread,
        onAction: (reference) => setItemKey(referenceKey(reference)),
      }}
    />
  );

  return (
    <form
      onSubmit={submit}
      aria-busy={optionsQuery.isLoading}
      // The pack keeps to the right edge, as it does beside a conversation, and at
      // the same sort of width: stretched to fill the card it was enormous. The
      // fields take about 32rem, which is as wide as a form of four controls wants
      // to be, and centre in what is left.
      className="grid w-full min-w-0 lg:min-h-[--pack-chat-panel-height] lg:grid-cols-[minmax(0,1fr)_minmax(300px,26rem)]"
    >
      {/* The column has the card's stable height from the first loading frame, so
          centring does not change when the pack arrives and makes the grid taller.
          It stays pinned while a long pack scrolls. */}
      <Stack
        fullWidth
        gap="md"
        className="mx-auto min-w-0 max-w-[32rem] px-5 py-5 lg:sticky lg:top-0 lg:h-[--pack-chat-panel-height] lg:justify-center lg:self-start"
      >
        <Stack fullWidth gap="none">
          <Typography variant="subtitleW2">Start a thread</Typography>
          <Typography variant="bodyW3" textColor="secondary">
            Choose the technician, then click the figure on their PayPack.
          </Typography>
        </Stack>

        {unavailable ?? (
          <>
            <Field label="Technician">
              <AutoComplete
                ariaLabel="Technician"
                placeholder={
                  optionsQuery.isLoading
                    ? "Loading technicians…"
                    : "Search technicians"
                }
                emptyMessage="No technician matches."
                selectedValue={selectedTechnician?.id ?? ""}
                searchValue={technicianSearch}
                items={technicians.map((technician) => ({
                  value: technician.id,
                  label: technician.name,
                }))}
                fullWidth
                containScroll
                disabled={optionsQuery.isLoading}
                scrollAreaClassName="max-h-72"
                onSearchValueChange={setTechnicianSearch}
                onSelectedValueChange={(value) => {
                  setTechnicianId(value);
                  setItemKey(undefined);
                }}
              />
            </Field>

            {selectedItem ? (
              <SelectedStatementItemPreview item={selectedItem} />
            ) : (
              <Typography
                variant="bodyW3"
                textColor="tertiary"
                className="w-full border-y border-stroke-tertiary py-3"
              >
                {items.length > 0
                  ? `No figure picked yet. Click one of the ${items.length} on the pack.`
                  : "No figure picked yet."}
              </Typography>
            )}

            <Field label="Message">
              <Textarea
                aria-label="Thread message"
                value={body}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Explain what you're checking on, or what you changed"
                disabled={optionsQuery.isLoading || startThread.isPending}
                className="min-h-[6rem] resize-y pt-2 text-md placeholder:text-md"
                onChange={(event) => setBody(event.target.value)}
              />
            </Field>
          </>
        )}

        <Stack direction="row" gap="sm">
          <Button
            type="submit"
            size="sm"
            loading={startThread.isPending}
            disabled={
              !selectedTechnician ||
              !activeRunId ||
              !selectedItem ||
              !body.trim()
            }
          >
            Start thread
          </Button>
          <Button
            type="button"
            size="sm"
            variant="text"
            disabled={startThread.isPending}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>

      {/* The pack sits where the pack always sits, on the right, so starting a
          thread looks like reading one. Its own hairline rather than the form's
          padding: the pack is a column of its own and runs to the pane edge. */}
      <Stack
        gap="none"
        align="stretch"
        className="min-w-0 border-t border-stroke-tertiary lg:border-l lg:border-t-0"
      >
        {pickerPane}
      </Stack>
    </form>
  );
};
